import { useRef, useState } from "react";
import type { KeyboardEvent } from "react";

import { Input } from "@monorepo-apps/ui/src/components/input";
import { cn } from "@monorepo-apps/ui/src/lib/utils";

interface EditableTitleProps {
  value: string;
  canWrite: boolean;
  onChange: (next: string) => void;
  className?: string;
  ariaLabel: string;
}

/**
 * A title that writers edit in place: double-click or Enter to edit, Enter or blur to save,
 * Escape to cancel. An empty title is refused.
 */
export function EditableTitle({
  value,
  canWrite,
  onChange,
  className,
  ariaLabel,
}: EditableTitleProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  // Escape unmounts the input, which still fires blur — this keeps that blur from saving.
  const cancelled = useRef(false);

  if (!editing) {
    if (!canWrite) return <span className={className}>{value}</span>;
    const startEditing = () => {
      setDraft(value);
      cancelled.current = false;
      setEditing(true);
    };
    // Double-click for the mouse; Enter or F2 for the keyboard (a focusable button).
    return (
      <span
        role="button"
        tabIndex={0}
        aria-label={`${ariaLabel}: ${value}. Press Enter to rename`}
        className={cn(
          className,
          "hover:bg-muted/60 focus-visible:ring-primary/40 cursor-text rounded focus-visible:ring-2 focus-visible:outline-none"
        )}
        onDoubleClick={startEditing}
        onKeyDown={(e: KeyboardEvent<HTMLSpanElement>) => {
          if (e.key === "Enter" || e.key === "F2") {
            e.preventDefault();
            startEditing();
          }
        }}
        title="Double-click or press Enter to rename"
      >
        {value}
      </span>
    );
  }

  return (
    <Input
      autoFocus
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      aria-label={ariaLabel}
      className={cn("h-8 max-w-md text-[14px] font-semibold", className)}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
        if (e.key === "Escape") {
          cancelled.current = true;
          setEditing(false);
        }
      }}
      onBlur={() => {
        if (cancelled.current) return;
        const next = draft.trim();
        if (next && next !== value) onChange(next);
        setEditing(false);
      }}
    />
  );
}
